import { LocationStrategy } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import {
  NavigationEnd,
  NavigationExtras,
  Params,
  Router,
} from '@angular/router';
import { AssetsService } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  AccessCondition,
  B2CAccessConditionFFBName,
  B2CAccessConditionFFName,
  B2CAccessConditionName,
  B2CAccessConditionNameEnum,
  B2CActionOnFailEnum,
  B2CRouteName,
  B2CRouting,
  B2CRoutingPage,
  BrandFeatureName,
  CTAQueryStateType,
  Dossier,
  FeatureName,
  RoutingContext,
  SpecialActionName,
  SpecialRedirectionCommand,
  WeatherEvent,
  canBookWithInsurance,
} from '@sc/data-access-models';
import {
  DossierSandboxService,
  FittersSandboxService,
} from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import { AuthOktaService } from '@sc/feature-auth-okta';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { ScriptManagerService } from '@sc/feature-script-manager';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  AppointmentUtils,
  DeclarationConditions,
  ScreenUtils,
  dayjs,
} from '@sc/utils';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  connectable,
  forkJoin,
  from,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  skipWhile,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  B2CRouterVerbose,
  B2C_ROUTER_VERBOSE_TOKEN,
} from '../tokens/router-debugging.token';
import { normalizeUrlPath } from '../utils';

/**
 * This service allows to navigate between the pages that are referenced in the B2C routing config
 * It takes into account special actions such as going to next/previous page, conditions to check if any page is accessible and possible redirections
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerRouterService {
  private static readonly OpenArrivalBaseUrl = '/open-arrival';

  /**
   * Prefix used in navigation options state keys to reference url param values to pass in the page url
   */
  private static readonly NavigationStateUrlParsingPrefix = 'b2c-routing-';
  /**
   * Navigation options state key used to tell the router service if the page should be openned in a new browser tab
   */
  private static readonly NavigationStateUrlNewTab = '--b2c-routing-new-tab';
  /**
   * Navigation options state key used to tell the router service if the query params of the previous page should be preserved on the new page
   */
  private static readonly NavigationStateUrlPreserveQuery =
    '--b2c-routing-preserve-query';

  /**
   * Navigation options state key used internally by the router service to know when to skip redirection tests if they have already been done
   */
  private static readonly NavigationStateIsRedirection =
    '--b2c-routing-is-redirect';

  /**
   * Navigation options state key used internally by the router to remember the #next command that triggered a navigation or a page test
   */
  private static readonly NavigationIsNextCommand =
    '--b2c-routing-is-next-command';
  /**
   * Navigation options state key used internally by the router to remember the #previous command that triggered a navigation or a page test
   */
  private static readonly NavigationIsPreviousCommand =
    '--b2c-routing-is-previous-command';
  /**
   * Navigation options state key used internally by the router to remember the #last-accessible command that triggered a navigation or a page test
   */
  private static readonly NavigationIsLastAccessibleCommand =
    '--b2c-routing-is-last-accessible-command';
  /**
   * Navigation options state key used internally by the router to remember the #history-back command that triggered a navigation or a page test
   */
  private static readonly NavigationIsHistoryBackCommand =
    '--b2c-routing-is-history-back-command';

  /**
   * Getter for NavigationStateUrlParsingPrefix
   * @see NavigationStateUrlParsingPrefix
   */
  static getStateKey(urlParam: string) {
    return `${CustomerRouterService.NavigationStateUrlParsingPrefix}${urlParam}`;
  }

  /**
   * Getter for NavigationStateUrlNewTab
   * @see NavigationStateUrlNewTab
   */
  static getStateNewTab() {
    return `${CustomerRouterService.NavigationStateUrlNewTab}`;
  }

  /**
   * Getter for NavigationStateUrlPreserveQuery
   * @see NavigationStateUrlPreserveQuery
   */
  static getStatePreserveQuery() {
    return `${CustomerRouterService.NavigationStateUrlPreserveQuery}`;
  }

  private static getContextFromState(
    routingState: NavigationExtras['state']
  ): RoutingContext {
    return {
      isCommandPrevious:
        routingState?.[CustomerRouterService.NavigationIsPreviousCommand],
      isCommandNext:
        routingState?.[CustomerRouterService.NavigationIsNextCommand],
      isCommandLastAccessible:
        routingState?.[CustomerRouterService.NavigationIsLastAccessibleCommand],
      isCommandHistoryBack:
        routingState?.[CustomerRouterService.NavigationIsHistoryBackCommand],
    };
  }

  /**
   * Log levels and their associated console functions in this service
   */
  private static readonly logFuncMap: Record<
    B2CRouterVerbose,
    (message?: any, ...optionalParams: any[]) => void
  > = {
    0: () => {},
    1: console.error,
    2: console.warn,
    3: console.info || console.log,
    4: console.debug,
  };

  /**
   * A map associating each existing routing condition name to the function that checks it
   */
  private readonly conditionNameFuncMap: Record<
    B2CAccessConditionNameEnum,
    (dossier: Dossier, routingContext: RoutingContext) => boolean
  > = {
    'has-stored-selected-fitter': this.checkHasStoredSelectedFitter.bind(this),
    'user-can-book-appointment': this.checkUserCanBookAppointment.bind(this),
    'decl-is-glass-infos-filled': this.checkGlassInfosFilled.bind(this),
    'decl-is-car-set': this.checkIsCarSet.bind(this),
    'decl-is-license-plate-set': this.checkIsLicensePlateSet.bind(this),
    'decl-is-glass-set': this.checkIsGlassSet.bind(this),
    'car-has-unknown-adas': this.checkCarHasUnknownAdas.bind(this),
    'decl-is-adas-set': this.checkDeclIsAdasSet.bind(this),
    'decl-is-adas': this.checkDeclIsAdas.bind(this),
    'decl-is-sinister-date-set': this.checkIsSinisterDateSet.bind(this),
    'has-search-criteria-address':
      this.checkHasSearchCriteriaAddress.bind(this),
    'mofification-cutoff-allowed': this.checkModifCutoffAllowed.bind(this),
    'cancel-cutoff-allowed': this.checkCancelCutoffAllowed.bind(this),
    'not-modified': this.checkNotModified.bind(this),
    'not-cancelled': this.checkNotCancelled.bind(this),
    'dossier-id-exists': this.checkDossierIdExists.bind(this),
    'appointment-id-exists': this.checkAppointmentIdExists.bind(this),
    'decl-windshield-glass': this.checkIsWindshieldGlass.bind(this),
    'decl-is-repairable-windshield-glass-impact-type':
      this.checkIsReparableWindshieldImpactType.bind(this),
    'decl-is-repairable-windshield-glass-impact-size':
      this.checkIsReparableWindshieldImpactSize.bind(this),
    'decl-is-mirror-glass': this.checkIsMirrorGlass.bind(this),
    'decl-is-repair-operation': this.checkIsRepairOperation.bind(this),
    'decl-is-replace-operation': this.checkIsReplaceOperation.bind(this),
    'has-no-status-appointment': this.checkNoStatusAppointment.bind(this),
    'not-requested-or-booked': this.checkNotRequestedOrBooked.bind(this),
    'dossier-is-complete': this.checkIsDossierComplete.bind(this),
    'is-requested-appointment': this.checkIsRequestAppointment.bind(this),
    'callback-car-type': this.checkIsCallbackCarType.bind(this),
    'callback-glass-choice': this.checkIsCallbackGlass.bind(this),
    'declaration-end-glass-choice': this.declarationEndGlass.bind(this),
    'is-dossier-callback': this.checkIsCallbackDossier.bind(this),
    'is-dossier-slots-suggested': this.checkIsDossierSlotsSuggested.bind(this),
    'is-dossier-covered': this.checkIsDossierCovered.bind(this),
    'no-previous': this.checkNoPrevious.bind(this),
    'no-next': this.checkNoNext.bind(this),
    'no-last-accessible': this.checkNoLastAccessible.bind(this),
    'only-stored-next': this.checkOnlyStoredNext.bind(this),
    'is-dossier-created-by-insurer':
      this.checkIsDossierCreatedByInsurer.bind(this),
    'is-mobile': this.checkIsMobile.bind(this),
    'is-landscape': this.checkIsLandscape.bind(this),
    'is-desktop': this.checkIsDesktop.bind(this),
    'from-specific-page': this.checkFromSpecificPage.bind(this),
    'has-weather-events': this.checkWeatherEvents.bind(this),
    'is-agenda-origin': this.isAgendaOrigin.bind(this),
    'can-edit-dossier-after-api-creation':
      this.checkCanEditDossierAfterApiCreation.bind(this),
  };

  private readonly actionOnFailMap: Record<B2CActionOnFailEnum, () => void> = {
    'modification-cutoff-snackbar': () =>
      this.actionModificationCutoffSnackbar(),
    'cancel-cutoff-snackbar': () => this.actionCancelCutoffSnackbar(),
  };

  private readonly specialActionMap: Record<
    SpecialActionName,
    (dossier: Dossier) => Observable<null>
  > = {
    'operation-dialog': this.actionOperationTypeDialog.bind(this),
    'adas-dialog': this.actionAdasDialog.bind(this),
    'weather-info-dialog': this.actionWeatherInfoDialog.bind(this),
  };

  private readonly pageHistory$ = new BehaviorSubject<
    {
      page: B2CRoutingPage;
      options?: NavigationExtras;
    }[]
  >([]);

  /**
   * Current dossier stored in sandbox
   */
  private readonly currentDossier$: Observable<Dossier>;
  /**
   * Current routing config taken from json of the right insurer
   */
  private readonly routingPages$: Observable<B2CRouting>;
  /**
   * Snapshot of the latest routing config, used by synchronous access-condition checks.
   */
  private routingSnapshot: B2CRouting | null = null;
  /**
   * The page currently being displayed on the browser
   */
  private readonly currentPage$ = new BehaviorSubject<B2CRoutingPage>(null);
  /**
   * Indicate if the last location change was done through push events (namely the navigation arrows of the browser)
   */
  private readonly lastNavIsPushed$ = new BehaviorSubject<boolean>(false);
  /**
   * Tells if the current page allows the display of weather events alerts
   */
  private readonly displayWeatherEvents$: Observable<boolean>;

  /**
   * Tells if the router should stop the events comming form the browser's navigation buttons
   */
  private shouldPreventBrowserNav = false;
  /**
   * Tells if the browser navigation buttons have already been put on listener
   */
  private browserNavListenerCreated = false;

  /**
   * The verbose used in this service logs
   */
  private readonly routerVerbose: B2CRouterVerbose;

  /**
   * The current state of CTA queries
   */
  private storedFitter: CTAQueryStateType;

  /**
   * WeatherEvents stored in sandbox
   */
  private eventFitterData: WeatherEvent[];
  constructor(
    private readonly featureFlipService: FeatureFlippingManagerService,
    private readonly locationStrategy: LocationStrategy,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslatorService,
    private readonly configurationService: ConfigurationService,
    private readonly dossierSB: DossierSandboxService,
    private readonly fitterSB: FittersSandboxService,
    private readonly router: Router,
    private readonly authService: AuthOktaService,
    private readonly assetsService: AssetsService,
    private readonly declarationConditions: DeclarationConditions,
    private readonly scriptManager: ScriptManagerService,
    private readonly overlayService: AdaptableOverlayService,
    @Inject(B2C_ROUTER_VERBOSE_TOKEN)
    @Optional()
    overrideRouterVerbose: B2CRouterVerbose | null
  ) {
    // Init service log verbose
    this.routerVerbose = overrideRouterVerbose || B2CRouterVerbose.Warn;

    this.displayWeatherEvents$ = this.currentPage$.pipe(
      map((page) => !!page?.additionalOptions?.displayWeatherEvent)
    );

    // Init dossier and do not take it while it is fetching (otherwise, there might be initial routing errors occuring)
    this.currentDossier$ = combineLatest([
      this.dossierSB.dossier$,
      this.dossierSB.dossierFetching$,
    ]).pipe(
      skipWhile(([dossier, isFetching]) => !!isFetching),
      map(([dossier, isFetching]) => dossier),
      tap((dossier) => this.log('Current dossier :', [dossier]))
    );

    // Retrieve the state of CTA queries
    this.fitterSB?.ctaQueryState$?.subscribe((fitterCTA) => {
      this.storedFitter = fitterCTA;
      this.log('New CTA state :', [this.storedFitter]);
      if (!fitterCTA?.fitterId && fitterCTA?.fitterName) {
        this.fitterSB
          .getFitterIdByName(fitterCTA?.fitterName)
          .subscribe((fitter) => {
            this.storedFitter.fitterId = fitter?.fitterId;
            this.storedFitter.brandId = fitter?.brandId;
          });
      }
    });

    // Listen to navigation events to know if the user pushed a new state
    this.router.events.subscribe((routingEvent) => {
      if (routingEvent instanceof NavigationEnd) {
        this.lastNavIsPushed$.next(false);
      }
    });

    this.locationStrategy.onPopState((e) => {
      this.lastNavIsPushed$.next(true);
      this.getPageFromUrl(window.location.pathname)
        .pipe(take(1))
        .subscribe((page) => this.currentPage$.next(page));
    });

    // Retrieve the current routing config
    this.routingPages$ = this.assetsService.getRoutingCustomer().pipe(
      filter((routing) => !!routing),
      tap((routing) => (this.routingSnapshot = routing)),
      tap((routing) => this.log('Loaded routing config :', [routing]))
    );

    // Init the resize listener for redirections if screen size is not supported on some pages
    window.addEventListener('resize', (event) => {
      this.onResizeCheckConditions(event);
    });

    // Retrieve dossier from backend
    this.loadDossier();

    // Call navigation process on the first page after loading the app so that if we land on a page that we shouldn't see, we are redirected
    this.navigateOnAppLoad();

    this.fitterSB.weatherEvents$?.subscribe(
      (events) => (this.eventFitterData = events)
    );
  }

  get routingConfig() {
    return this.routingPages$;
  }

  /**
   * External getter for the current page
   */
  get currentPage() {
    return this.currentPage$.pipe(filter((page) => !!page));
  }

  getCurrentPageValue() {
    return this.currentPage$.getValue();
  }

  /**
   * External getter for the push state
   */
  get lastNavIsPushed() {
    return this.lastNavIsPushed$;
  }

  /**
   * The default logger function for this service
   * @param {string} str The string to be logged
   * @param {any[]} args A list of any additional value that will be passed to the logger function
   * @param {B2CRouterVerbose} level The logging level of this call
   */
  private log(
    str: string,
    args: any[] = [],
    level: B2CRouterVerbose = B2CRouterVerbose.Debug
  ) {
    // Do not log if log level is lesser than verbose level
    if (this.routerVerbose < level) {
      return;
    }

    // Call log with a "little" formatting
    CustomerRouterService.logFuncMap[level](
      `<${new Date().toISOString()}>[B2C][Router] ${
        Object.keys(B2CRouterVerbose)?.find(
          (key) => B2CRouterVerbose?.[key] === level
        ) || ''
      }: ${str}`,
      ...args
    );
  }

  private logGroup(openGroup: true, grpName: string);
  private logGroup(openGroup: false);
  private logGroup(openGroup: boolean, grpName?: string) {
    // Do not group if log level is lesser than warn
    if (this.routerVerbose >= B2CRouterVerbose.Warn) {
      return;
    }

    if (openGroup) {
      console.group(grpName);
    } else {
      console.groupEnd();
    }
  }

  /**
   * Loads the dossier from backend using the stored dossierId
   */
  private loadDossier() {
    const dossierNumber = this.authService.getDossierNumber();

    this.log('Loading dossier :', [dossierNumber]);

    this.dossierSB.dossier$
      .pipe(
        take(1),
        switchMap((currentDossier) => {
          if (!!dossierNumber && !currentDossier) {
            return this.dossierSB.fetchDossier(dossierNumber).pipe(
              take(1),
              map(({ dossier }) => dossier as Dossier)
            );
          }

          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Method used to navigate to the page where we land on app init.
   * If the navigation is impossible, we are redirected like any classic navigation from this service
   */
  private navigateOnAppLoad() {
    const usedUrl = window.location.pathname;

    this.log('Loading app ... Routing starts at :', [usedUrl]);

    this.getPageFromUrl(usedUrl)
      .pipe(take(1))
      .subscribe(
        (page) => {
          this.currentPage$.next(page);
          // Force open-arrival action if no action and token has been set
          const forcedQueryParam: Params = {};
          if (page.name === B2CRouteName.CTA) {
            if (
              !/[?&]action=/.test(window.location.search) &&
              !/[?&]token=/.test(window.location.search) &&
              !/cta\/.*/.test(window.location.pathname)
            ) {
              forcedQueryParam.action = 'open-arrival';
            }
          }

          // Since we are forcing a new navigation even if this results on the same page, query params must be preserved if they were given on app init
          this.navigateToPage(page, {
            queryParams: {
              ...forcedQueryParam,
            },
            state: {
              [CustomerRouterService.getStatePreserveQuery()]: true,
            },
          });
        },
        (err) =>
          this.log(
            'Routing service could not initialize current page on app load :',
            [err],
            B2CRouterVerbose.Error
          )
      );
  }

  /**
   * Method that checks if a page is currently authorized given the screen size, and redirects if not.
   * @param {UIEvent} resizeEvent The event originating from the window resize listener
   */
  private onResizeCheckConditions(resizeEvent: UIEvent) {
    if (!this.checkScreenDependencies(this.currentPage$.getValue())) {
      this.log('Redirection caused by resize from page :', [
        this.currentPage$.getValue(),
      ]);

      // NOTE - Maybe checking the redirection page before triggering navigation is overkill, since navigation will detect the resize condition of the current page
      this.getRedirectionPage(
        this.currentPage$.getValue()?.screenDependencies?.onResizeRedirect,
        this.currentPage$.getValue()
      ).subscribe((redirectInfos) =>
        this.navigateToPage(redirectInfos.page, redirectInfos.options)
      );
    }
  }

  /**
   * Method that adds a new navigation state in the history and keeps a limited number in the observable
   * @param pageInfos The navigation infos (page/options) to add in the latest history
   */
  private addPageInHistory(pageInfos: {
    page: B2CRoutingPage;
    options?: NavigationExtras;
  }) {
    if (!pageInfos?.page) {
      return;
    }

    const currentHistoryState = [...this.pageHistory$.getValue()];

    if (currentHistoryState.length >= 100) {
      currentHistoryState.shift();
    }

    currentHistoryState.push(pageInfos);

    this.pageHistory$.next(currentHistoryState);
  }

  /**
   * Method that adds a new navigation state in the history and keeps a limited number in the observable
   * @param pageInfos The navigation infos (page/options) to add in the latest history
   */
  private removePagesInHistory(nbToRemove: number) {
    const currentHistoryState = [...this.pageHistory$.getValue()];

    for (let i = 0; i < nbToRemove; i++) {
      currentHistoryState.pop();
    }

    this.pageHistory$.next(currentHistoryState);
  }

  /**
   * Method that returns the page/options at the given index in the history
   * @param {number} nbPages The number of pages to go back in the pages history
   * @returns {{ page: B2CRoutingPage; options?: NavigationExtras; }} The page and options used to navigate in in the past
   */
  private getPreviousPageInHistory(nbPages = 1) {
    if (nbPages < 1) {
      return throwError(
        () => new Error(`Cannot retrieve ${nbPages}th page in router history`)
      );
    }

    return this.pageHistory$.pipe(
      map((pageInfosList) => {
        // Last page of list is always the current page so we avoid it by taking the previous one
        let indexOfPage = pageInfosList.length - 1 - nbPages;
        if (indexOfPage < 0) {
          indexOfPage = 0;
        } else if (indexOfPage >= pageInfosList.length - 1) {
          indexOfPage = pageInfosList.length - 2;
        }

        return pageInfosList[indexOfPage];
      }),
      map((navInfos) => ({
        ...navInfos,
        options: {
          ...navInfos?.options,
          state: {
            ...navInfos?.options?.state,
            [CustomerRouterService.NavigationIsHistoryBackCommand]: true,
          },
        },
      })),
      tap((navInfos) => this.log('Getting page in history :', [navInfos]))
    );
  }

  /**
   * Method that returns the ordered list of URL params referenced in the URL of a page config (prfixed by ':')
   * @param {string} pageUrl The URL template referenced in a page config
   * @returns {string[]} The names of the URL params of pageUrl
   */
  private getPageUrlParamNames(pageUrl: string) {
    const urlParamsList = pageUrl
      .match(/:([^\/]*)/g)
      ?.map((param) => param.slice(1));

    this.log('Found param names in URL :', [pageUrl, urlParamsList]);

    return urlParamsList;
  }

  /**
   * Method that analyzes a given URL and infers which values were applied to its URL params, given a corresponding page config
   * @param {string} pageUrl The effective URL from which we want to retrieve the potential URL param values
   * @param {B2CRoutingPage} page The page corresponding to pageUrl, in which we will compare pageUrl with the list of URL templates
   * @returns {Record<string, string>} The list of values applied to the URL params of the given URL: pageUrl
   */
  private getPageUrlParamValues(pageUrl: string, page?: B2CRoutingPage) {
    const resultParamValues: Record<string, string> = {};

    const usedPage = page || this.currentPage$.getValue();

    if (!usedPage) {
      return resultParamValues;
    }

    // Find which URL template of the page corresponds to the given URL
    const usedUrlTemplate = this.getCorrespondingUrlTemplate(pageUrl, usedPage);
    if (pageUrl.indexOf('?') > 0) {
      // if query param are no urlencode, they can contains '/'
      pageUrl = pageUrl.substring(0, pageUrl.indexOf('?'));
    }
    if (usedUrlTemplate) {
      if (this.isOpenArrivalBaseAlias(pageUrl, usedPage)) {
        this.log('Found param values in URL :', [
          usedUrlTemplate,
          resultParamValues,
        ]);

        return resultParamValues;
      }

      // After finding the template URL, split it in different fragments and
      // test if each fragment is at the emplacement of a URL param referenced in the template URL
      const splittedUrlTemplate = usedUrlTemplate.split('/');
      const splittedUrl = pageUrl.split('/');

      if (splittedUrlTemplate.length === splittedUrl.length) {
        splittedUrlTemplate.forEach((urlTemplateFragment, i) => {
          if (urlTemplateFragment.startsWith(':')) {
            resultParamValues[urlTemplateFragment.slice(1)] = splittedUrl[i];
          }
        });
      } else {
        this.log(
          `B2C Routing could not match current URL path (${pageUrl}) with any known routes : ${usedPage.urlList.join(
            ', '
          )}`,
          [],
          B2CRouterVerbose.Warn
        );
      }
    } else {
      this.log(
        `B2C Routing could not match current URL path (${pageUrl}) with any known routes : ${usedPage.urlList.join(
          ', '
        )}`,
        [],
        B2CRouterVerbose.Warn
      );
    }

    this.log('Found param values in URL :', [
      usedUrlTemplate,
      resultParamValues,
    ]);

    return resultParamValues;
  }

  /**
   * Method that generates a URL from a page and navigation options
   * @param {B2CRoutingPage} page The page from which the result URL will be based
   * @param {NavigationExtras} options The options to use when formatting the result URL
   * @returns {string} A usable URL formatted from the given page and options
   */
  private getPageUrl(page: B2CRoutingPage, options?: NavigationExtras) {
    // Find the right URL by replacing each known URL param by the value given in options state.
    // Then keep the URLs in which all params were replaced and take the one that has the most params
    const usedUrlPath = page?.urlList
      ?.map((url) => {
        const urlParams = this.getPageUrlParamNames(url);

        let resultUrl = url;
        let nbParamsFound = 0;
        urlParams?.forEach((urlParam) => {
          const paramOptionKey = CustomerRouterService.getStateKey(urlParam);
          if (paramOptionKey in (options?.state || {})) {
            nbParamsFound++;
            resultUrl = resultUrl.replace(
              `:${urlParam}`,
              options.state[paramOptionKey]
            );
          }
        });

        return { resultUrl, nbParamsFound };
      })
      .filter((urlInfos) => !urlInfos.resultUrl.includes(':'))
      .sort(
        (urlInfosA, urlInfosB) =>
          urlInfosB.nbParamsFound - urlInfosA.nbParamsFound
      )[0]?.resultUrl;

    // If asked, preserve query params from previous url
    const url = new URL(usedUrlPath, window.location.origin);

    if (options?.state?.[CustomerRouterService.getStatePreserveQuery()]) {
      Object.keys(options?.queryParams || {}).forEach((queryKey) => {
        url.searchParams.set(queryKey, options.queryParams[queryKey]);
      });
    }

    const finalUrlString = url.pathname;

    this.log('Inferred URL from page and options :', [
      finalUrlString,
      page,
      options,
    ]);

    return finalUrlString;
  }

  /**
   * Method that retrieves a URL template from a given page and that matches with a given URL
   * @param {string} url The URL used to compare and find the right URL template (/!\ should not contain any query param)
   * @param {B2CRoutingPage} page The page from which we want to retrieve the right URL template
   * @returns {string} The URL template that matches the given URL
   */
  private getCorrespondingUrlTemplate(url: string, page: B2CRoutingPage) {
    const usedUrl = url.split('?')[0];

    const usedUrlTemplate =
      page.urlList.find((urlTemplate) => {
        const regexFormattedUrlTemplate = urlTemplate.replace(/:[^\/]*/g, '.*'); // Replace params with any string
        const regex = new RegExp(`^${regexFormattedUrlTemplate}$`);

        return regex.test(usedUrl);
      }) || this.getOpenArrivalAliasUrlTemplate(usedUrl, page);

    this.log('Found URL template from real URL :', [
      usedUrlTemplate,
      url,
      page,
    ]);

    return usedUrlTemplate;
  }

  private getOpenArrivalAliasUrlTemplate(url: string, page: B2CRoutingPage) {
    if (!this.isOpenArrivalBaseAlias(url, page)) {
      return null;
    }

    return page.urlList.find((urlTemplate) => {
      const normalizedUrlTemplate = normalizeUrlPath(urlTemplate);

      return (
        !!normalizedUrlTemplate &&
        normalizedUrlTemplate !== CustomerRouterService.OpenArrivalBaseUrl &&
        normalizedUrlTemplate.startsWith(
          `${CustomerRouterService.OpenArrivalBaseUrl}/`
        )
      );
    });
  }

  private isOpenArrivalBaseAlias(url: string, page: B2CRoutingPage) {
    return (
      page.name === B2CRouteName.OpenArrival &&
      normalizeUrlPath(url) === CustomerRouterService.OpenArrivalBaseUrl
    );
  }

  /**
   * Method that retrieves a page form the routing conf, given its name
   * @param {B2CRouteName} pageName The name of the page that we want to retrieve
   * @returns {Observable<B2CRoutingPage>} The page that corresponds to the given name
   */
  private getPageFromName(pageName: B2CRouteName) {
    return this.routingPages$.pipe(
      map((routing) => routing.pages.find((page) => page.name === pageName)),
      tap((page) => this.log('Found page from name :', [page]))
    );
  }

  public getPageUrlFromName(pageName: B2CRouteName) {
    return this.getPageFromName(pageName).pipe(
      map((page) => this.getPageUrl(page))
    );
  }

  /**
   * Method that searches the page in routing config that has a URL template that matches with a given URL
   * @param {string} url The URL to match to a page
   * @returns {Observable<B2CRoutingPage>} The page that matches best with the given URL
   */
  public getPageFromUrl(url: string | null) {
    if (!url) {
      return of(null);
    }
    const usedUrl = url.replace(/\/[^\/]*\.html/, '');

    return this.routingPages$.pipe(
      map((routing) => {
        if (usedUrl === '/' || usedUrl === '') {
          // Forced override of empty path that always lead to empty cta (and then open-arrival)
          return (
            routing.pages.find((page) => page.name === B2CRouteName.CTA) ||
            routing.pages[0]
          );
        }

        const resultPage = routing.pages.find((page) => {
          const foundUrlTemplate = this.getCorrespondingUrlTemplate(
            usedUrl,
            page
          );

          return !!foundUrlTemplate;
        });

        return resultPage || routing.pages[0]; // If no page found, return the first of the list, often Unauthorized
      }),
      tap((page) => this.log('Found page from URL :', [usedUrl, page]))
    );
  }

  //#region Public Navigation functions
  /**
   * Main method of navigation that must be called by every other derived navigation method in this service.
   * Allows to navigate to a given page, with given navigation options.
   * @param {B2CRoutingPage} page The page to which we want to navigate
   * @param {NavigationExtras} options The options to apply to the navigations
   * @returns {Observable<boolean>} An observable of the navigation success state
   */
  navigateToPage(
    page: B2CRoutingPage,
    options?: NavigationExtras
  ): Observable<boolean> {
    this.logGroup(true, 'Router Navigation Process');
    this.log('Begin navigation process', [page, options]);
    const resultNav$ = this.currentDossier$
      .pipe(
        take(1),
        // If no page is given, then throw error and go to Unauthorized or first page
        tap(() => {
          if (!page) {
            this.log(
              'No page provided for navigation, defaulting to Unauthorized',
              [],
              B2CRouterVerbose.Warn
            );
            throw 'unauthorized';
          }
        }),

        // First, check if the wanted page can be accessed
        map((currentDossier) => {
          // If the caller of this function is already a redirection process, then there is no need to re-check it
          if (
            options?.state?.[CustomerRouterService.NavigationStateIsRedirection]
          ) {
            return true;
          }

          return this.checkAccessConditionsList(
            page.accessConditions,
            currentDossier,
            CustomerRouterService.getContextFromState(options?.state)
          );
        }),
        tap((canAccess) =>
          this.log('After checking access conditions :', [canAccess])
        ),

        // Then apply redirections if needed
        switchMap((canAccess) => {
          const usedOptions: NavigationExtras =
            this.getDefaultOptionsForNavigation(
              this.currentPage$.getValue(),
              `${window.location.pathname}${window.location.search}`,
              options
            );

          let navInfos$: Observable<{
            page: B2CRoutingPage;
            options: NavigationExtras;
          }>;

          // If we can access the page, then used the given page/options
          // If we can't, then get the redirection page from the condition that failed and deduce new page/options
          if (canAccess === true) {
            navInfos$ = of({
              page,
              options: usedOptions,
            });
          } else if (typeof canAccess !== 'boolean') {
            if (canAccess.actionOnFail) {
              //We add a delay to let the change language in the app from dossier selectedLanguage before showing the snackbar
              of(true)
                .pipe(delay(300))
                .subscribe(() => {
                  this.actionOnFailMap[canAccess.actionOnFail]?.();
                });
            }
            navInfos$ = this.getRedirectionPage(
              canAccess.redirectOnFail,
              page
            ).pipe(
              map((redirectionInfos) => ({
                page: redirectionInfos.page,
                options: {
                  ...usedOptions,
                  ...redirectionInfos.options,
                },
              }))
            );
          } else {
            this.log(
              `Routing could not navigate to page ${page.name} but found no redirection logic. Staying on same page ...`
            );
            navInfos$ = of({ page: null, options: null });
          }

          return navInfos$;
        }),
        tap((navInfos) =>
          this.log('After applying potential redirections :', [navInfos])
        ),

        // Then, apply the navigation in the right form
        switchMap((navInfos) => {
          if (navInfos.page) {
            // Get the URL to use for navigation
            const pageUrl = this.getPageUrl(navInfos.page, navInfos.options);

            if (
              navInfos.options?.state?.[
                CustomerRouterService.NavigationStateUrlNewTab
              ]
            ) {
              // Opening new tab is requested through options state
              const serializedUrl = this.router.serializeUrl(
                this.router.createUrlTree([pageUrl], navInfos.options)
              );

              this.log(
                'Calling navigation on new tab :',
                [serializedUrl],
                B2CRouterVerbose.Infos
              );

              window.open(serializedUrl, '__blank');

              return of(true);
            } else {
              this.log(
                'Calling navigation :',
                [pageUrl, navInfos.options],
                B2CRouterVerbose.Infos
              );

              // Do not save page navigation in history for sepcial cases, only for classic navigation through angular router
              this.addPageInHistory({
                page: navInfos.page,
                options: navInfos.options,
              });

              // Basic navigation process
              return from(
                this.router.navigate([pageUrl], navInfos.options)
              ).pipe(
                catchError((err) => {
                  this.log(
                    'Internal Navigation Error :',
                    [err],
                    B2CRouterVerbose.Error
                  );
                  return throwError(() => err);
                }),
                tap(() => this.currentPage$.next(navInfos.page)),
                tap(() => {
                  if (
                    navInfos.page.additionalOptions?.triggerScriptIds?.length
                  ) {
                    this.scriptManager.callScripts(
                      navInfos.page.additionalOptions.triggerScriptIds
                    );
                  }

                  this.scriptManager.injectMetaTag(navInfos.page.name);
                })
              );
            }
          } else {
            return of(false);
          }
        }),
        take(1),
        catchError((err) => {
          if (page) {
            this.log('Navigation Error :', [err], B2CRouterVerbose.Error);
          }

          const redirectToUnauthorized$ = combineLatest([
            this.routingPages$,
            this.getPageFromName(B2CRouteName.Unauthorized),
          ]).pipe(
            take(1),
            switchMap(([routing, page]) => {
              // In case we recieve a reason for the error that can be interpreted by Unauthorized page, we should pass it in the options here
              const pageUrl = this.getPageUrl(page || routing.pages[0], {});

              return from(this.router.navigate([pageUrl]));
            }),
            take(1)
          );

          return redirectToUnauthorized$;
        })
      )
      .pipe(tap(() => this.logGroup(false)));

    return this.triggerNavigationFromObservable(resultNav$);
  }

  /**
   * Method that allows to navigate to the nearest page in history order
   * @param {NavigationExtras} options Navigation options to apply
   * @returns {Observable<boolean>} An observable of the navigation success state
   */
  navigateBack(options?: NavigationExtras) {
    this.logGroup(true, 'Navigation to Previous History Page');
    this.log('Begin navigation process (Back History)', [options]);

    const resultNav$ = this.getPreviousPageInHistory().pipe(
      map((navInfos) => ({
        page: navInfos.page,
        options: {
          ...navInfos.options,
          ...options,
          state: {
            ...navInfos?.options?.state,
            ...options?.state,
            [CustomerRouterService.NavigationIsHistoryBackCommand]: true,
          },
        },
      })),
      take(1),
      switchMap((navInfos) =>
        this.navigateToPage(navInfos.page, navInfos.options)
      ),
      take(1),
      // After navigation, we want to remove the last 2 pages in history : the one we were on and the one we navigated to (duplicated in history since we were "going back")
      tap(() => this.removePagesInHistory(2)),
      tap(() => this.logGroup(false))
    );

    return this.triggerNavigationFromObservable(resultNav$);
  }

  /**
   * Method that allows to navigate to a precise page, given its name and navigation options
   * @param {B2CRouteName} pageName The name of the page we wnat to navigate to
   * @param {NavigationExtras} options The options to use for navigation
   * @returns {Observable<boolean>} An observable of the navigation success state
   */
  navigateToPageByName(
    pageName: B2CRouteName,
    options?: NavigationExtras
  ): Observable<boolean> {
    this.logGroup(true, 'Navigation to Page By Name');
    this.log('Begin navigation process (By Name)', [pageName, options]);

    const resultNav$ = this.getPageFromName(pageName).pipe(
      take(1),
      switchMap((usedPage) => this.navigateToPage(usedPage, options)),
      take(1),
      tap(() => this.logGroup(false))
    );

    return this.triggerNavigationFromObservable(resultNav$);
  }

  /**
   * Method that allows to navigate to the last accessible page of the declaration journey
   * @param {boolean} beginFromFirstPage Tells if the search for the last accessible page should begin at the first known page (Maybe remove ?)
   * @returns {Observable<boolean>} An observable of the navigation success state
   */
  navigateToLastAccessiblePage(
    beginFromFirstPage = true,
    options?: NavigationExtras
  ): Observable<boolean> {
    this.logGroup(true, 'Navigation to Last Accessible Page');
    this.log('Begin navigation process (Last Accessible)', [
      beginFromFirstPage,
    ]);

    const resultNav$ = this.routingPages$.pipe(
      take(1),
      switchMap((routing) =>
        this.getLastAccessiblePage(
          beginFromFirstPage ? routing.pages[0] : this.currentPage$.getValue()
        )
      ),
      switchMap((lastAccessiblePage) =>
        this.navigateToPage(lastAccessiblePage, {
          ...options,
          state: {
            ...options?.state,
            [CustomerRouterService.NavigationIsLastAccessibleCommand]: true,
          },
        })
      ),
      take(1),
      tap(() => this.logGroup(false))
    );

    return this.triggerNavigationFromObservable(resultNav$);
  }

  /**
   * Method that allows to navigate to the next available page found after the current page in routing order
   * @param {NavigationExtras} options The options to use for navigation
   * @returns {Observable<boolean>} An observable of the navigation success state
   */
  navigateToNextPage(options?: NavigationExtras): Observable<boolean> {
    this.logGroup(true, 'Navigation to Next Page');
    this.log('Begin navigation process (Next)', [options]);

    const resultNav$ = combineLatest([
      this.currentDossier$,
      this.getNextAccessiblePage(),
    ]).pipe(
      switchMap(([dossier, nextPageNavigable]) => {
        const currentPage = this.currentPage$?.getValue();
        const actionObs$: Observable<boolean>[] = [];
        if (currentPage?.specialActions?.length) {
          for (const action of currentPage.specialActions) {
            const actionConditionList = action.conditions;

            const checkResult = this.checkAccessConditionsList(
              actionConditionList,
              dossier,
              {
                isCommandNext: true,
              }
            );
            if (checkResult === true) {
              actionObs$.push(this.specialActionMap[action?.name]?.(dossier));
            }
          }
        }

        if (actionObs$?.length) {
          return forkJoin(actionObs$).pipe(
            map(() => {
              return nextPageNavigable;
            })
          );
        } else {
          return of(nextPageNavigable);
        }
      }),
      switchMap((nextPageNavigable) => {
        if (nextPageNavigable) {
          return this.navigateToPage(nextPageNavigable, {
            ...options,
            state: {
              ...options?.state,
              [CustomerRouterService.NavigationIsNextCommand]: true,
            },
          });
        } else {
          this.log(
            'No next page found for navigation',
            [options],
            B2CRouterVerbose.Error
          );
          return throwError(() => new Error('No next page available'));
        }
      }),
      take(1),
      tap(() => this.logGroup(false))
    );

    return this.triggerNavigationFromObservable(resultNav$);
  }

  /**
   * Method that allows to navigate to the next available page found before the current page in routing order
   * @param {NavigationExtras} options The options to use for navigation
   * @returns {Observable<boolean>} An observable of the navigation success state
   */
  navigateToPreviousPage(options?: NavigationExtras): Observable<boolean> {
    this.logGroup(true, 'Navigation to Previous Page');
    this.log('Begin navigation process (Previous)', [options]);

    const currentPage = this.currentPage$.getValue();
    const previousRedirect = currentPage?.specialRedirections?.find(
      (redirect) => redirect.name === 'previous-redirect'
    );

    if (
      previousRedirect?.pageName === SpecialRedirectionCommand.HistoryBack &&
      this.pageHistory$.getValue().length > 1
    ) {
      return this.navigateBack(options);
    }

    return this.navigateToSequentialPreviousPage(options);
  }

  private navigateToSequentialPreviousPage(options?: NavigationExtras) {
    const resultNav$ = this.getAdjacentPage(-1, undefined, {
      isCommandPrevious: true,
    }).pipe(
      switchMap((previousPageNavigable) => {
        if (previousPageNavigable) {
          return this.navigateToPage(previousPageNavigable, {
            ...options,
            state: {
              ...options?.state,
              [CustomerRouterService.NavigationIsPreviousCommand]: true,
            },
          });
        } else {
          this.log(
            'No previous page found for navigation',
            [options],
            B2CRouterVerbose.Error
          );
          return throwError(() => new Error('No previous page available'));
        }
      }),
      take(1),
      tap(() => this.logGroup(false))
    );

    return this.triggerNavigationFromObservable(resultNav$);
  }
  //#endregion Public Navigation functions

  /**
   * Method that transforms a navigation observable into a connectable observable an directly connects to it, triggering the navigation process
   * @param {Observable<boolean>} navObs Observable of the navigation success state that will be triggered by this function
   * @returns {Observable<boolean>} An observable that is already subscribed to, and therefore has triggered navigation inside of it
   */
  private triggerNavigationFromObservable(
    navObs: Observable<boolean>
  ): Observable<boolean> {
    const resultNavObs = connectable(navObs);
    resultNavObs.connect();

    return resultNavObs;
  }

  /**
   * Generic method that allows to search for the next accessible page, begining from a given page
   * @param {(currentPage: B2CRoutingPage) => boolean} predicate A function that tells if a given page is accessible, in addition to the generic condition checks
   * @param {number} increment The increment telling in which direction to search for the next page inside the routing config
   * @param {B2CRoutingPage} page The page from which we begin the search
   * @param {{ [key in B2CAccessConditionName]?: boolean }} extraConditions Extra conditions to check, in addition to the predicate and the generic condition checks, to determine if a page is allowed
   * @returns {Observable<B2CRoutingPage>} The page that was found to be the nearest accessible page
   */
  private getAdjacentPage(
    increment: number,
    page?: B2CRoutingPage,
    routingContext: RoutingContext = {}
  ) {
    return combineLatest([this.routingPages$, this.currentDossier$]).pipe(
      take(1),
      map(([routing, usedDossier]) => {
        const usedPage = page || this.currentPage$.getValue();
        // Get the current page index in the routing config list, and add the increment to start the search
        let currentIndex =
          routing?.pages?.findIndex(
            (searchingPage) => searchingPage.name === usedPage?.name
          ) + increment;

        this.log(
          `Searching for available adjacent page with increment ${increment} :`,
          [page, routingContext]
        );

        let accesiblePage: B2CRoutingPage;
        // For each tested page, test if it is accessible through generic condition list / screen dependencies
        // If the page is accessible, break the loop and return it
        for (
          currentIndex;
          currentIndex >= 0 && currentIndex < routing.pages.length;
          currentIndex += increment
        ) {
          const adjacentPage = routing.pages[currentIndex];

          this.log('Testing if page is accessible :', [adjacentPage]);

          const accessResult = this.canNavigateToPage(
            adjacentPage,
            usedDossier,
            routingContext
          );

          if (accessResult === true) {
            this.log('Tested page is accessible');
            accesiblePage = adjacentPage;
            break;
          }
          this.log('Tested page is NOT accessible');
        }

        return accesiblePage;
      }),
      take(1)
    );
  }

  /**
   * Method that retrieves the nearest accessible page located before (in routing config order) the given page
   * @param {B2CRoutingPage} page The page from which we start the search
   * @param {{ [key in B2CAccessConditionName]?: boolean }} extraConditions Conditions to add to the checks when searching the previous page
   * @returns {Observable<B2CRoutingPage>} The nearest accessible page before the given page
   */
  getPreviousAccesiblePage(
    page?: B2CRoutingPage,
    routingContext: RoutingContext = { isCommandPrevious: true }
  ) {
    const currentPage = page || this.currentPage$.getValue();
    const previousRedirect = currentPage?.specialRedirections?.find(
      (redirect) => redirect.name === 'previous-redirect'
    );

    if (
      previousRedirect?.pageName === SpecialRedirectionCommand.HistoryBack &&
      this.pageHistory$.getValue().length > 1
    ) {
      return this.getPreviousPageInHistory().pipe(
        take(1),
        map((navInfos) => navInfos.page)
      );
    }

    return this.getAdjacentPage(-1, page, routingContext);
  }

  /**
   * Method that retrieves the nearest accessible page located after (in routing config order) the given page
   * @param {B2CRoutingPage} page The page from which we start the search
   * @param {{ [key in B2CAccessConditionName]?: boolean }} extraConditions Conditions to add to the checks when searching the next page
   * @returns {Observable<B2CRoutingPage>} The nearest accessible page after the given page
   */
  getNextAccessiblePage(
    page?: B2CRoutingPage,
    routingContext: RoutingContext = { isCommandNext: true }
  ) {
    return this.getAdjacentPage(1, page, routingContext);
  }

  /**
   * Method that retrieves the nearest accessible page located after (in routing config order) the current page, in order to update dossier declaration page
   * @returns {Observable<B2CRoutingPage>} The nearest accessible page =
   */
  getNextAccessiblePageForDossierSteps() {
    return combineLatest([
      this.currentPage$,
      this.routingPages$,
      this.currentDossier$,
    ]).pipe(
      map(([currentPage, routingPages, dossier]) => {
        const nextPageIndex = routingPages?.pages?.findIndex(
          (page) => page?.name === currentPage?.name
        );
        const dossierPageIndex = routingPages?.pages?.findIndex(
          (page) => page?.name === dossier?.declarationPage
        );
        const dossierPage = routingPages.pages.find(
          (page) => page?.name === dossier?.declarationPage
        );
        const canUpdatePage =
          (nextPageIndex >= dossierPageIndex ||
            dossierPage?.additionalOptions?.skippableStepperInfo) &&
          (dossier?.state === 'INIT' ||
            dossier?.state === 'QUALIFIED' ||
            dossier?.state === 'SLOT_SEARCH');
        return canUpdatePage
          ? currentPage
          : routingPages?.pages?.find(
              (page) => page?.name === dossier?.declarationPage
            );
      }),
      take(1)
    );
  }

  /**
   * Method that retrieves the last page (in the order of the routing config) that can be accessed with the current state
   * @param {B2CRoutingPage} beginPage The page from which to to begin the search
   * @returns {Observable<B2CRoutingPage>} The last page that is navigable in the routing config order
   */
  getLastAccessiblePage(beginPage?: B2CRoutingPage) {
    const lastAccessiblePage = beginPage || this.currentPage$.getValue();

    return combineLatest([
      of(lastAccessiblePage),
      this.routingPages$.pipe(take(1)),
      this.currentDossier$.pipe(take(1)),
    ]).pipe(
      map(([currentPage, routing, currentDossier]) => {
        this.log('Next accessible page (searching for last) :', [currentPage]);

        const currentPageIndex = routing.pages.findIndex(
          (p) => p.name === currentPage.name
        );

        /*
          Search in pages until we encounter a non-skippable and non-accessible page
          then the last accessible page is the last accessible encountered before this page
          Ex : A = accessible page / N = non-accessible non-skippable page / S = non-accessible skippable page
          A1 --- A2 --- S3 --- A4 --- N5 --- A6 --- N7
          In this exemple, the last accessible page is A4
        */
        let adjacentPage = routing.pages[currentPageIndex + 1];
        let pageToReturn: B2CRoutingPage = currentPage;
        let isSearchingForValidPage = true;
        do {
          if (!adjacentPage) {
            // No adjacent page is found while searching for one, end the search here (end of the page list)
            isSearchingForValidPage = false;
          } else {
            // Found an adjacent page, test if we can access it
            const accessResult = this.canNavigateToPage(
              adjacentPage,
              currentDossier,
              { isCommandLastAccessible: true }
            );

            // If we can access it, then save it as the current last accessible
            if (accessResult === true) {
              pageToReturn = adjacentPage;
            } else if (
              !adjacentPage?.accessConditions?.some(
                (condition) =>
                  condition.name === 'no-last-accessible' &&
                  !!condition.successValue
              ) &&
              !adjacentPage.additionalOptions?.skippableOnLastAccessible &&
              typeof accessResult === 'object' &&
              accessResult.redirectOnFail !== 'next-redirect'
            ) {
              // If we can't access it and can't skip it, then end the search here and return the last saved page
              isSearchingForValidPage = false;
            }
          }

          // If the adjacent page is accessible or if we can skip it, then continue the search and move the adjacent page to the next iteration
          const adjacentPageIndex = routing.pages.findIndex(
            (p) => p.name === adjacentPage?.name
          );
          adjacentPage = routing.pages[adjacentPageIndex + 1];
        } while (isSearchingForValidPage);

        return pageToReturn;
      }),
      take(1),
      tap((page) => this.log('Found last accessible page :', [page]))
    );
  }

  /**
   * Method that tells if there exist a page that is accessible before the given page (in routing config order)
   * @param {B2CRoutingPage} page The page from which to start the search
   * @returns {Observable<boolean>} If there is a page accessible before the given page
   */
  canNavigatePrevious(page?: B2CRoutingPage) {
    return this.getPreviousAccesiblePage(
      page || this.currentPage$.getValue()
    ).pipe(map((previousPage) => !!previousPage));
  }

  /**
   * Method that tells if there exist a page that is accessible after the given page (in routing config order)
   * @param {B2CRoutingPage} page The page from which to start the search
   * @returns {Observable<boolean>} If there is a page accessible after the given page
   */
  canNavigateNext(page?: B2CRoutingPage) {
    return this.getNextAccessiblePage(
      page || this.currentPage$.getValue()
    ).pipe(map((nextPage) => !!nextPage));
  }

  /**
   * Method that tells if there exist a page in the navigation history
   * @returns {Observable<boolean>} If there is a page accessible in the history
   */
  canNavigateBack() {
    return combineLatest([
      this.getPreviousPageInHistory(),
      this.currentPage$.pipe(take(1)),
    ]).pipe(
      map(
        ([previousPageInfos, currentPage]) =>
          !!previousPageInfos?.page &&
          previousPageInfos.page.name !== currentPage.name
      )
    );
  }

  /**
   * Method that tells if a given page is currently accessible
   * @param {B2CRoutingPage} page The page we want to access
   * @returns {boolean | AccessCondition} If the given page is accessible
   */
  canNavigateToPageByName(
    pageName: B2CRouteName,
    dossier?: Dossier,
    routingContext: RoutingContext = {}
  ) {
    return combineLatest([
      this.getPageFromName(pageName),
      this.currentDossier$.pipe(take(1)),
    ]).pipe(
      take(1),
      map(
        ([page, currentDossier]) =>
          !!page &&
          this.checkScreenDependencies(page) &&
          this.checkAccessConditionsList(
            page.accessConditions,
            dossier || currentDossier,
            routingContext
          )
      )
    );
  }

  /**
   * Method that tells if a given page is currently accessible
   * @param {B2CRoutingPage} page The page we want to access
   * @returns {boolean | AccessCondition} If the given page is accessible
   */
  canNavigateToPage(
    page: B2CRoutingPage,
    dossier: Dossier,
    routingContext: RoutingContext = {}
  ) {
    return (
      this.checkScreenDependencies(page) &&
      this.checkAccessConditionsList(
        page.accessConditions,
        dossier,
        routingContext
      )
    );
  }

  /**
   * Method that generates new navigation options from the current page/URL, to use in any new navigation
   * @param {B2CRoutingPage} currentPage The current page before the navigation to which we want to apply the generated options
   * @param {string} currentPageUrl The current URL of the page before the navigation to which we want to apply the generated options
   * @param {NavigationExtras} additionalOptions Additional options to add to the generated ones
   * @returns {NavigationExtras} New default options to use as a base for any navigation starting from the given page
   */
  private getDefaultOptionsForNavigation(
    currentPage: B2CRoutingPage,
    currentPageUrl: string,
    additionalOptions?: NavigationExtras
  ) {
    const navDefaultOptions: NavigationExtras = {
      state: {},
      queryParams: {},
      ...additionalOptions,
    };

    // By default, use stored infos for url params
    if (this.checkHasStoredSelectedFitter()) {
      navDefaultOptions.state[CustomerRouterService.getStateKey('fitterId')] =
        this.storedFitter.fitterId;
      navDefaultOptions.state[CustomerRouterService.getStateKey('brandId')] =
        this.storedFitter.brandId;
    }

    // By default, reuse url params from previous url
    const currentUrlParamValues = this.getPageUrlParamValues(
      currentPageUrl,
      currentPage
    );
    Object.keys(currentUrlParamValues).forEach(
      (paramKey) =>
        (navDefaultOptions.state[CustomerRouterService.getStateKey(paramKey)] =
          currentUrlParamValues[paramKey])
    );

    // If asked, preserve query params from previous url
    if (
      navDefaultOptions?.state?.[CustomerRouterService.getStatePreserveQuery()]
    ) {
      const url = new URL(currentPageUrl, window.location.origin);

      url.searchParams.forEach(
        (queryValue, queryKey) =>
          (navDefaultOptions.queryParams[queryKey] = queryValue)
      );
    }

    this.log('Default navigation options generated :', [navDefaultOptions]);

    return navDefaultOptions;
  }

  /**
   * Method that retrieves the page/options to use for a navigation that was redirected by a given action and from a given page/URL
   * @param {string} redirectAction The redirection action triggered by a failed condition
   * @param {B2CRoutingPage} currentPage The current page from which the redirection starts
   * @param {string} currentUrlPath The current URL from which the redirection starts
   * @returns {Observable<{page: B2CRoutingPage; options: NavigationExtras;}>} The new page/options to use for a redirected navigation
   */
  private getRedirectionPage(
    redirectAction: string,
    currentPage: B2CRoutingPage,
    currentUrlPath?: string
  ): Observable<{
    page: B2CRoutingPage;
    options: NavigationExtras;
  }> {
    this.log('Searching for redirection page', [
      redirectAction,
      currentPage,
      currentUrlPath,
    ]);

    // First, naïvely get the page designated by the redirect action
    return this.getPageFromAction(
      redirectAction,
      currentPage,
      this.getDefaultOptionsForNavigation(
        currentPage,
        currentUrlPath || `${window.location.pathname}${window.location.search}`
      )
    ).pipe(
      take(1),
      switchMap((redirectInfos) => {
        if (!redirectInfos.page) {
          // If no redirection page is found, then we must use the default redirection listed in the given page and get the new page from this
          this.log('Trying to retrieve default redirection ...');

          const usedSpecialRedirect = currentPage?.specialRedirections?.find(
            (redirect) => redirect.defaultRedirect
          );

          if (usedSpecialRedirect) {
            const pageFromActionRedirect$ = this.getPageFromAction(
              usedSpecialRedirect.name,
              currentPage,
              this.getDefaultOptionsForNavigation(
                currentPage,
                currentUrlPath ||
                  `${window.location.pathname}${window.location.search}`,
                {
                  ...redirectInfos.options,
                  ...usedSpecialRedirect?.options,
                }
              )
            );

            return pageFromActionRedirect$;
          } else {
            this.log(
              `Could not find any page allowing redirection from ${currentPage?.name} with action ${redirectAction} or default. Staying on same page ...`,
              [],
              B2CRouterVerbose.Warn
            );
            return of({
              page: currentPage,
              options: null,
            });
          }
        } else {
          // If a redirection page is found, then return it
          return of({
            page: redirectInfos.page,
            options: redirectInfos.options,
          });
        }
      }),
      map(
        (redirectInfos: {
          page: B2CRoutingPage;
          options: NavigationExtras;
        }) => ({
          ...redirectInfos,
          options: {
            ...redirectInfos?.options,
            state: {
              ...redirectInfos?.options?.state,
              [CustomerRouterService.NavigationStateIsRedirection]: true,
            },
          },
        })
      ),
      tap((redirectInfos) =>
        this.log('Final page after redirection process :', [redirectInfos])
      )
    );
  }

  /**
   * Method that gets the page directly designated by a redirection action, without fallback
   * @param {string} action The redirection action triggered by a failed condition
   * @param {B2CRoutingPage} page The current page from which the redirection starts
   * @param {NavigationExtras} options The options to add to the default redirection options
   * @returns {Observable<{page: B2CRoutingPage; options: NavigationExtras;}>} The page/options found to use for a redirected navigation
   */
  private getPageFromAction(
    action: string,
    page: B2CRoutingPage,
    options?: NavigationExtras
  ) {
    return this.routingPages$.pipe(
      switchMap((routing) => {
        let redirectPage: Observable<B2CRoutingPage>;
        let redirectOptions: Observable<NavigationExtras> = of(options);

        // Get the redirection action object from the given action name
        const usedSpecialRedirect = page?.specialRedirections?.find(
          (special) => special.name === action
        );

        this.log('Found special redirect action :', [usedSpecialRedirect]);

        if (usedSpecialRedirect) {
          redirectOptions = of({
            ...redirectOptions,
            ...usedSpecialRedirect.options,
          });

          // Deduce the page/options from the pageName of the redirection action object (can be a # command)
          if (usedSpecialRedirect.pageName === SpecialRedirectionCommand.Next) {
            redirectPage = this.getNextAccessiblePage(page);
          } else if (
            usedSpecialRedirect.pageName === SpecialRedirectionCommand.Previous
          ) {
            redirectPage = this.getPreviousAccesiblePage(page);
          } else if (
            usedSpecialRedirect.pageName ===
            SpecialRedirectionCommand.LastAccessible
          ) {
            redirectPage = this.getLastAccessiblePage(page);
          } else if (
            usedSpecialRedirect.pageName ===
            SpecialRedirectionCommand.HistoryBack
          ) {
            if (this.pageHistory$.getValue().length > 1) {
              redirectPage = this.getPreviousPageInHistory().pipe(
                take(1),
                map((routing) => routing.page)
              );
              redirectOptions = this.getPreviousPageInHistory().pipe(
                take(1),
                map((routing) => routing.options)
              );
            } else {
              redirectPage = this.getAdjacentPage(-1, page, {
                isCommandPrevious: true,
              });
            }
          } else {
            const foundPage = routing.pages.find(
              (page) => page?.name === usedSpecialRedirect?.pageName
            );

            redirectPage = of(foundPage);
          }
        } else {
          redirectPage = of(null);
        }

        return combineLatest([redirectPage, redirectOptions]).pipe(
          map(([page, options]) => ({
            page,
            options,
          })),
          tap((redirectInfos) =>
            this.log('Page found from redirect action :', [redirectInfos])
          )
        );
      })
    );
  }

  /**
   * Method that checks a list of conditions (and all their subcondition trees) against the state of a dossier to know if a page is accessible.
   * @param {AccessCondition[]} conditions List of conditions to check against the given dossier
   * @param {Dossier} dossier The current state of a dossier that will be checked for page access
   * @returns {boolean | AccessCondition} Either the access condition that failed when checking the dossier or a boolean telling if we can access the page
   */
  private checkAccessConditionsList(
    conditions: AccessCondition[] = [],
    dossier: Dossier,
    routingContext: RoutingContext
  ): boolean | AccessCondition {
    this.logGroup(true, 'Page Conditions Check');
    let canAccess = true;
    let failedCondition: AccessCondition;

    this.log('Checking access conditions on dossier :', [conditions, dossier]);

    for (let index = 0; index < conditions.length; index++) {
      const currentCondition = conditions[index];

      failedCondition = undefined;
      // Primary condition check
      canAccess = this.checkCondition(
        currentCondition,
        dossier,
        routingContext
      );

      // SubConditions check zone
      if (!canAccess && currentCondition.subConditionsOnFail?.length) {
        // Subconditions check on primary condition fail
        this.log('Subconditions check on primary condition fail');

        const resultCheckCondition = this.checkAccessConditionsList(
          currentCondition.subConditionsOnFail,
          dossier,
          routingContext
        );
        canAccess =
          typeof resultCheckCondition === 'boolean'
            ? (resultCheckCondition as boolean)
            : canAccess;
        failedCondition =
          typeof resultCheckCondition === 'boolean'
            ? null
            : (resultCheckCondition as AccessCondition);
      } else if (currentCondition.subConditionsOnSuccess?.length && canAccess) {
        // Subconditions check on primary condition success
        this.log('Subconditions check on primary condition success');

        const resultCheckCondition = this.checkAccessConditionsList(
          currentCondition.subConditionsOnSuccess,
          dossier,
          routingContext
        );
        canAccess =
          typeof resultCheckCondition === 'boolean'
            ? (resultCheckCondition as boolean)
            : canAccess;
        failedCondition =
          typeof resultCheckCondition === 'boolean'
            ? null
            : (resultCheckCondition as AccessCondition);
      } else if (!canAccess) {
        // Primary condition fail without subcondition
        this.log('Primary condition fail without subcondition');

        failedCondition = currentCondition;
      }

      if (failedCondition) {
        break;
      }
    }

    this.log('Accessible or failed condition :', [canAccess, failedCondition]);
    this.logGroup(false);

    return failedCondition ? failedCondition : canAccess;
  }

  /**
   * Method that checks an individual access condition on a given dossier
   * @param {AccessCondition} condition Individual condition to check on a given dossier
   * @param {Dossier} currentDossier The dossier on which the condition will be checked
   * @returns {boolean} If the given dossier meets the given condition
   */
  private checkCondition(
    condition: AccessCondition,
    currentDossier: Dossier,
    routingContext: RoutingContext = {}
  ): boolean {
    let canAccess = true;

    // FeatureFlips check zone
    if (this.isConditionFeatureFlip(condition.name)) {
      canAccess =
        this.checkFeatureFlip(condition.name, currentDossier) ===
        condition?.successValue;
    } else {
      // Other conditions check zone
      canAccess =
        this.conditionNameFuncMap[condition.name]?.(currentDossier, {
          ...routingContext,
          accessConditionOptions: condition?.options,
        }) === condition?.successValue;
    }

    this.log('Checking single condition :', [
      condition,
      currentDossier,
      canAccess,
    ]);

    return canAccess;
  }

  /**
   * Method that tells if a given page can be shown on the current screen size
   * @param page The page on which we want to test screen conditions
   * @returns If the page can be shown on the current screen size
   */
  private checkScreenDependencies(page: B2CRoutingPage) {
    return (
      (page?.screenDependencies?.allowedScreens?.includes('Desktop') &&
        ScreenUtils?.isDesktopResolution()) ||
      (page?.screenDependencies?.allowedScreens?.includes('Landscape') &&
        ScreenUtils?.isLandScapeResolution()) ||
      (page?.screenDependencies?.allowedScreens?.includes('Mobile') &&
        ScreenUtils?.isMobileResolution())
    );
  }

  canDisplayWeatherEventAlert() {
    return this.displayWeatherEvents$;
  }

  /**
   * Method that allows the browser navigation events to be stopped, by launching the listener if it's not already done
   */
  preventBrowserNav(onNavBlocked?: () => void) {
    if (!this.browserNavListenerCreated) {
      this.preventBackButton(onNavBlocked);
      this.browserNavListenerCreated = true;
    }

    this.shouldPreventBrowserNav = true;
  }

  /**
   * Method that lets the browser navigation event act as normal
   */
  allowBrowserNav() {
    this.shouldPreventBrowserNav = false;
  }

  /**
   * Method that prevents the user from returning to a previous page by listening on browser navigation button events
   */
  private preventBackButton(onNavBlocked?: () => void) {
    history.pushState(null, null, location.href);
    this.locationStrategy.onPopState((state) => {
      if (!this.shouldPreventBrowserNav) {
        return;
      }

      onNavBlocked?.();

      history.pushState(null, null, location.href);
    });
  }

  //#region Condition checker definitions
  private isConditionFeatureFlip(
    conditionName: B2CAccessConditionName
  ): conditionName is B2CAccessConditionFFName | B2CAccessConditionFFBName {
    return /^FF\/.*$/.test(conditionName);
  }

  private isConditionBrandFeatureFlip(
    conditionName: B2CAccessConditionName
  ): conditionName is B2CAccessConditionFFBName {
    return /^FF\/B\/.*$/.test(conditionName);
  }

  private getFeatureFromConditionName(
    conditionName: B2CAccessConditionFFName
  ): FeatureName;
  private getFeatureFromConditionName(
    conditionName: B2CAccessConditionFFBName
  ): BrandFeatureName;
  private getFeatureFromConditionName(
    conditionName: B2CAccessConditionFFName | B2CAccessConditionFFBName
  ): BrandFeatureName | FeatureName {
    const splittedName = conditionName.split('/');

    return splittedName[splittedName.length - 1] as any;
  }

  private checkFeatureFlip(
    conditionFeatureName: B2CAccessConditionName,
    currentDossier: Dossier
  ) {
    if (this.isConditionFeatureFlip(conditionFeatureName)) {
      if (this.isConditionBrandFeatureFlip(conditionFeatureName)) {
        const feature = this.getFeatureFromConditionName(conditionFeatureName);
        return this.featureFlipService.isBrandFeatureActivated(
          currentDossier?.appointment?.brandId ??
            currentDossier?.requestAppointment?.brandId,
          feature
        );
      } else {
        const feature = this.getFeatureFromConditionName(conditionFeatureName);
        return this.featureFlipService.isFeatureActivated(feature);
      }
    }

    return false;
  }

  private checkNoNext(currentDossier: Dossier, routingContext: RoutingContext) {
    return !routingContext?.isCommandNext;
  }

  private checkNoPrevious(
    currentDossier: Dossier,
    routingContext: RoutingContext
  ) {
    return !routingContext?.isCommandPrevious;
  }

  private checkNoLastAccessible(
    currentDossier: Dossier,
    routingContext: RoutingContext
  ) {
    return !routingContext?.isCommandLastAccessible;
  }

  private checkNoHistoryBack(
    currentDossier: Dossier,
    routingContext: RoutingContext
  ) {
    return !routingContext?.isCommandHistoryBack;
  }

  private checkIsMobile() {
    return !!ScreenUtils?.isMobileResolution();
  }

  private checkIsLandscape() {
    return !!ScreenUtils?.isLandScapeResolution();
  }

  private checkIsDesktop() {
    return !!ScreenUtils?.isDesktopResolution();
  }

  private checkFromSpecificPage(
    currentDossier: Dossier,
    routingContext: RoutingContext
  ) {
    return (
      this.currentPage$.getValue()?.name ===
      routingContext?.accessConditionOptions?.page
    );
  }

  private checkOnlyStoredNext(
    currentDossier: Dossier,
    routingContext: RoutingContext
  ) {
    return (
      this.checkNoNext(currentDossier, routingContext) ||
      this.checkHasStoredSelectedFitter()
    );
  }

  private checkDossierIdExists(currentDossier: Dossier) {
    return !!currentDossier?.id;
  }

  private checkAppointmentIdExists(currentDossier: Dossier) {
    return this.declarationConditions.hasValidAppointment(currentDossier);
  }

  private checkModifCutoffAllowed(currentDossier: Dossier) {
    return (
      AppointmentUtils.applyModifCutOffFeature(
        currentDossier?.appointment?.time,
        this.configurationService.getConfig().cutOffOptions.modifCutOff
      ) ||
      currentDossier?.requestAppointment?.status === 'REQUESTED' ||
      currentDossier?.requestAppointment?.status === 'CANCELLED'
    );
  }

  private checkCancelCutoffAllowed(currentDossier: Dossier) {
    return (
      AppointmentUtils.applyCancelCutOffFeature(
        currentDossier?.appointment?.time,
        this.configurationService.getConfig().cutOffOptions.cancelCutOff
      ) || currentDossier?.requestAppointment?.status === 'REQUESTED'
    );
  }

  private canBypassOptionalSinisterDateCheck(
    currentDossier: Dossier,
    routingContext: RoutingContext = {}
  ) {
    const isOptionalSinisterDateActivated =
      this.featureFlipService.isFeatureActivated(
        'b2c-declaration-start-step-sinister-date'
      ) &&
      this.featureFlipService.isFeatureActivated(
        'b2c-declaration-start-step-sinister-date-not-required'
      );

    if (!isOptionalSinisterDateActivated) {
      return false;
    }

    const currentPageName = this.currentPage$.getValue()?.name;
    const isCustomerInformationsStep = (
      [
        B2CRouteName.CustomerInformations,
        B2CRouteName.CustomerInformationsForm,
      ] as B2CRouteName[]
    ).includes(currentPageName);

    // Treat the mobile form route and the desktop route as the same step for forward navigation.
    const isNavigatingForwardFromCustomerInformations =
      routingContext?.isCommandNext && isCustomerInformationsStep;

    const hasStartedGlassJourney = this.checkIsGlassSet(currentDossier);
    const isResumingJourneyFromCTA =
      routingContext?.isCommandLastAccessible && hasStartedGlassJourney;
    const isNavigatingBackWithinGlassJourney =
      routingContext?.isCommandPrevious && hasStartedGlassJourney;

    return (
      isNavigatingForwardFromCustomerInformations ||
      isResumingJourneyFromCTA ||
      isNavigatingBackWithinGlassJourney
    );
  }

  private checkIsSinisterDateSet(
    currentDossier: Dossier,
    routingContext: RoutingContext = {}
  ) {
    return (
      !!currentDossier?.claim?.sinisterDate ||
      this.canBypassOptionalSinisterDateCheck(currentDossier, routingContext)
    );
  }

  private checkIsCarSet(currentDossier: Dossier) {
    return this.declarationConditions.isCarSet(currentDossier);
  }

  private checkIsLicensePlateSet(currentDossier: Dossier) {
    return this.declarationConditions.hasLicensePlate(currentDossier);
  }

  private checkIsGlassSet(currentDossier: Dossier) {
    return this.declarationConditions.isGlassSet(currentDossier?.claim?.glass);
  }

  private checkCarHasUnknownAdas(currentDossier: Dossier) {
    return (
      !currentDossier?.claim?.car?.firstRegistration ||
      (dayjs(currentDossier.claim.car.firstRegistration).isSameOrAfter(
        '2016-01-01'
      ) &&
        dayjs(currentDossier.claim.car.firstRegistration).isBefore(
          '2022-07-01'
        ))
    );
  }

  private checkDeclIsAdasSet(currentDossier: Dossier) {
    return (
      currentDossier?.claim?.calibration !== undefined &&
      currentDossier?.claim?.calibration !== null
    );
  }

  private checkDeclIsAdas(currentDossier: Dossier) {
    return (
      currentDossier?.claim?.calibration === 'true' &&
      currentDossier?.claim?.glass?.glass === 'windshield'
    );
  }

  private checkIsWindshieldGlass(currentDossier: Dossier) {
    return this.declarationConditions.isGlassWindshield(
      currentDossier?.claim?.glass
    );
  }

  private checkIsReparableWindshieldImpactType(currentDossier: Dossier) {
    return this.declarationConditions.isRepairableWindshieldImpactType(
      currentDossier?.claim?.glass
    );
  }

  private checkIsReparableWindshieldImpactSize(currentDossier: Dossier) {
    return this.declarationConditions.isRepairableWindshieldImpactSize(
      currentDossier?.claim?.glass
    );
  }

  private checkIsRepairOperation(currentDossier: Dossier) {
    return this.declarationConditions.isRepairableWindshield(
      currentDossier?.claim?.glass
    );
  }

  private checkIsReplaceOperation(currentDossier: Dossier) {
    return (
      this.declarationConditions.isUnrepairableWindshiled(
        currentDossier?.claim?.glass
      ) ||
      !this.declarationConditions.isGlassWindshield(
        currentDossier?.claim?.glass
      )
    );
  }

  private checkIsMirrorGlass(currentDossier: Dossier) {
    return this.declarationConditions.isGlassMirrors(
      currentDossier?.claim?.glass
    );
  }

  private checkNoStatusAppointment(currentDossier: Dossier) {
    return !currentDossier?.appointment?.status;
  }

  private checkHasStoredSelectedFitter() {
    return (
      !!this.storedFitter?.fitterSelectionActive &&
      !!this.storedFitter?.fitterId &&
      !!this.storedFitter?.brandId
    );
  }

  private checkUserCanBookAppointment(currentDossier: Dossier) {
    return (
      this.declarationConditions.canBookAppointment(
        currentDossier?.claim?.glass
      ) &&
      canBookWithInsurance(
        currentDossier,
        {
          coverAllGlasses: this.featureFlipService.isFeatureActivated(
            'display-insurance-cover-for-all-glass'
          ),
          defaultIsInsuredWithoutGlass:
            this.featureFlipService.isFeatureActivated(
              'b2c-default-display-insured-without-glass'
            ),
        },
        this.featureFlipService.isFeatureActivated(
          'b2c-disable-booking-without-insurance'
        )
      )
    );
  }

  private isAgendaOrigin(currentDossier: Dossier) {
    return currentDossier?.creationData?.origin === 'AGENDA';
  }

  private checkGlassInfosFilled(currentDossier: Dossier) {
    return this.declarationConditions.isGlassChoiceFilled(currentDossier);
  }

  private checkHasSearchCriteriaAddress(currentDossier: Dossier) {
    return !!currentDossier?.searchCriteria?.address;
  }

  private checkNotModified(currentDossier: Dossier) {
    return !this.declarationConditions.isAppointmentModified(currentDossier);
  }

  private checkNotCancelled(currentDossier: Dossier) {
    return !this.declarationConditions.isAppointmentCancelled(currentDossier);
  }

  private checkIsDossierComplete(currentDossier: Dossier) {
    return this.declarationConditions.isDossierComplete(currentDossier);
  }

  private checkIsRequestAppointment(currentDossier: Dossier) {
    return this.declarationConditions.hasRequestedAppointment(currentDossier);
  }

  private checkIsCallbackCarType(currentDossier: Dossier) {
    return this.declarationConditions.isCarTypeCallbackType(currentDossier);
  }

  private checkIsCallbackGlass(currentDossier: Dossier) {
    return this.declarationConditions.isGlassCallbackType(currentDossier);
  }

  private declarationEndGlass(currentDossier: Dossier) {
    return this.declarationConditions.isGlassDeclarationEndType(currentDossier);
  }

  private checkIsCallbackDossier(currentDossier: Dossier) {
    return this.declarationConditions.isDossierCallbackType(currentDossier);
  }

  private checkNotRequestedOrBooked(currentDossier: Dossier) {
    return !this.declarationConditions.isDossierComplete(currentDossier);
  }

  private checkIsDossierSlotsSuggested(currentDossier: Dossier) {
    return (
      (currentDossier?.state === 'SLOT_SUGGESTED' ||
        currentDossier?.state === 'COMPLETE') &&
      currentDossier?.suggestedAppointments?.length > 0
    );
  }

  private checkIsDossierCovered(currentDossier: Dossier) {
    return !!currentDossier?.policyInformation?.isCovered;
  }

  private checkIsDossierCreatedByInsurer(currentDossier: Dossier) {
    return currentDossier?.creationData?.origin === 'B2B';
  }

  private checkWeatherEvents() {
    return this.eventFitterData.length > 0;
  }

  checkCanEditDossierAfterApiCreation(currentDossier: Dossier) {
    return this.declarationConditions.canEditB2CDossierAfterApiCreation(
      currentDossier
    );
  }
  //#endregion Condition checker definitions

  //#region Action on Fail definitions

  private actionModificationCutoffSnackbar() {
    this.snackbarService.showError(
      this.translateService.getInstantTranslation('Snackbar.errorTitle'),
      this.translateService.getInstantTranslation(
        'Snackbar.cannotModifyBooking',
        {
          nbHours:
            this.configurationService.getConfig().cutOffOptions.modifCutOff,
        }
      )
    );
  }

  private actionCancelCutoffSnackbar() {
    this.snackbarService.showError(
      this.translateService.getInstantTranslation('Snackbar.errorTitle'),
      this.translateService.getInstantTranslation(
        'Snackbar.cannotCancelBooking',
        {
          nbHours:
            this.configurationService.getConfig().cutOffOptions.cancelCutOff,
        }
      )
    );
  }

  private actionOperationTypeDialog(dossier: Dossier) {
    return this.overlayService.showOverlay({
      type: 'router-infos-action',
      input: {
        dossier: dossier,
        dialogType: 'operation-type-infos',
      },
    });
  }

  private actionAdasDialog(dossier: Dossier) {
    return this.overlayService.showOverlay({
      type: 'router-infos-action',
      input: {
        dossier: dossier,
        dialogType: 'adas-infos',
      },
    });
  }

  private actionWeatherInfoDialog() {
    return this.overlayService.showOverlay({
      type: 'router-infos-action',
      input: {
        dialogType: 'weather-infos',
      },
    });
  }

  //#endregion Action on Fail definitions
}
